import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/admin/_common/ui/table";
import ShowThumbnail from "@/app/admin/_common/ShowThumbnail";
import CuoEmpty from "@/app/admin/_common/CuoEmpty";
import { dayjsTz } from "@/app/_utils/dayjs";
import type { FestivalDetailShowRow } from "@/app/_services/FestivalService";
import { Drama } from "lucide-react";

const currencyFormat = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export default function FestivalShowsList({ shows }: { shows: FestivalDetailShowRow[] }) {
  const navigate = useNavigate();

  return (
    <div className="flex max-h-[420px] flex-col overflow-hidden rounded-md border border-border bg-card">
      <Table className="table-fixed" containerClassName="min-h-0 flex-1 overflow-y-auto">
        <TableHeader>
          <TableRow className="sticky top-0 z-10 border-b border-border hover:bg-muted/50 [&>th]:bg-background">
            <TableHead className="h-8 w-[28%] text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Show
            </TableHead>
            <TableHead className="h-8 w-[32%] text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Sold
            </TableHead>
            <TableHead className="h-8 w-[16%] text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Revenue
            </TableHead>
            <TableHead className="h-8 w-[24%] text-right text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Date
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {shows.length === 0 ? (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={4} className="h-[300px] p-0">
                <CuoEmpty icon={Drama} text="No shows in this festival yet" />
              </TableCell>
            </TableRow>
          ) : (
            shows.map((show) => (
              <TableRow
                key={show.id}
                className="group cursor-pointer border-border/50"
                onClick={() => navigate(`/admin/shows/${show.id}/details`)}
              >
                <TableCell className="py-2">
                  <div className="flex min-w-0 items-center gap-2.5">
                    <ShowThumbnail
                      src={show.img_url}
                      alt="show"
                      style={{ height: 32, width: 32, borderRadius: 6, flexShrink: 0 }}
                    />
                    <div className="min-w-0 leading-tight">
                      <div className="truncate text-[13.5px] text-foreground group-hover:underline">
                        {show.title || "N/A"}
                      </div>
                      {show.variant_name && (
                        <div className="truncate text-[12px] text-muted-foreground">
                          {show.variant_name}
                        </div>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="py-2">
                  <div className="flex items-center gap-2.5">
                    <div className="h-1.5 max-w-[160px] flex-1 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-foreground"
                        style={{
                          width: `${Math.min(100, show.capacity ? (show.sold / show.capacity) * 100 : 0)}%`,
                        }}
                      />
                    </div>
                    <span className="whitespace-nowrap text-[12px] font-medium text-muted-foreground">
                      {show.sold}/{show.capacity}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="whitespace-nowrap py-2">
                  {currencyFormat.format(show.revenue)}
                </TableCell>
                <TableCell className="whitespace-nowrap py-2 text-right text-muted-foreground">
                  {dayjsTz(show.start).format("MMM D, YYYY")}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
